import Badge from '@components/Badge';
import CheckedIcon from '@components/Checkbox/CheckedIcon';
import DefaultIcon from '@components/Checkbox/DefaultIcon';
import Column from '@components/Column';
import Divider from '@components/Divider';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { DiscountCouponRow } from '@models/discount';
import { getDDay } from '@utils/couponUtils';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
    coupon: DiscountCouponRow;
    isSelected: boolean;
    disabled?: boolean;
    /** 적용한 상품명 (상품 쿠폰) */
    targetName?: string;
    /** 적용 상품을 바꿀 수 있으면 '변경'을 보여준다 */
    onChangeTarget?: () => void;
    onToggle: () => void;
}

/** 쿠폰 한 장 (제목·D-day·만료일·체크) */
function CouponCard({
    coupon, isSelected, disabled, targetName, onChangeTarget, onToggle,
}: Props) {
    const { t } = useTranslation('order');
    const { title, expiresAt } = coupon;
    const dDay = getDDay(expiresAt);
    return (
        <Container data-selected={isSelected}
            aria-disabled={disabled}
            onClick={() => {
                if (!disabled) {
                    onToggle();
                }
            }} >
            <Row fullWidth justify='space-between' align='flex-start'>
                <Column spacing={2}>
                    <Text fontWeight='medium'
                        color={disabled ? colors.textUnselected : colors.textPrimary}>
                        {title}
                    </Text>
                    <Row spacing={2}>
                        {dDay !== undefined && (
                            <Badge color={!disabled && dDay <= 7 ? 'red' : 'gray'}>
                                D-{dDay}
                            </Badge>
                        )}
                        <Text size='body-long' fontWeight='medium' color={colors.textThird}>
                            {t('couponPage.until', { date: dayjs(expiresAt).format('YYYY.MM.DD') })}
                        </Text>
                    </Row>
                </Column>
                {!disabled && (isSelected ? <CheckedIcon/> : <DefaultIcon/>)}
            </Row>
            {(isSelected && targetName) && (
                <Column fullWidth spacing={2}>
                    <Divider isThin />
                    <Row fullWidth justify='space-between'>
                        <Text fontWeight='medium' color={colors.textPrimary}>{targetName}</Text>
                        {onChangeTarget && (
                            <ChangeButton type='button'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChangeTarget();
                                }}>
                                <Text size='body-long' color={colors.textSecondary}>
                                    {t('couponPage.change')}
                                </Text>
                            </ChangeButton>
                        )}
                    </Row>
                </Column>
            )}
        </Container>
    );
}

const Container = styled.div`
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    padding: var(--spacing-4);
    row-gap: var(--spacing-2);
    width: 100%;

    &[data-selected="true"] {
        background-color: var(--color-background-base);
        border-color: var(--color-icon-accent);
    }

    &[aria-disabled="true"] {
        cursor: default;
    }

    > div > svg {
        flex-shrink: 0;
    }
`;

const ChangeButton = styled.button`
    background: none;
    padding: 0;
`;

export default CouponCard;
